/**
 * Copy canónico del journey plan Crece (vos, “plan X”).
 * Una sola fuente para Activar / login / paywall / success / mail.
 * No acoplar a un procesador concreto en títulos de pantalla.
 */

export const PLAN_CRECE_NAME = 'plan Crece';
export const PLAN_GRATUITO_NAME = 'plan gratuito';
export const PLAN_ORIGINAL_NAME = 'plan original';

export const creceActivateCopy = {
  screenTitle: 'Activar plan Crece',
  eyebrow: 'Vuelto Crece',
  headline: 'Cuando tu negocio vende más',
  subtitle: 'Cancelás cuando quieras.',
  bullets: [
    'Ventas, pedidos y turnos ilimitados',
    'Sucursales ilimitadas',
    'Equipo con permisos',
    'Reportes avanzados y promociones',
    'Soporte prioritario',
  ] as const,
  /** Pie opcional Gratis → Crece */
  supportLine:
    'El plan Crece también ayuda a que Vuelto siga siendo gratis para quien arranca.',
  /** Fine print bajo el CTA: el abono cubre un negocio (las sucursales van dentro). */
  perOrgLine: 'El abono es por negocio, no por persona.',
  /** Trust row del checkout hosteado (proto): paga cualquier cuenta MP del dueño. */
  mpTrustLine: 'Pago seguro con Mercado Pago · cancelás cuando quieras',
  ctaPayMp: 'Pagar con Mercado Pago',
  ctaContinue: 'Continuar',
  ctaSecondary: 'Seguir con el plan gratuito',
  priceSuffix: '/ mes',
  resumeBannerTitle: 'No terminaste el pago',
  resumeBannerBody:
    'Mercado Pago quedó a medias. Podés retomarlo o seguir con el plan gratuito.',
  ctaResume: 'Retomá el pago en Mercado Pago',
  channelTitle: '¿Cómo preferís pagar?',
  channelSubtitle: 'Mismo plan Crece. Elegí el canal que te quede cómodo.',
  channelIapTitle: 'App Store / Play',
  channelIapBody: 'Suscripción de la tienda. Se gestiona en tu cuenta Apple o Google.',
  channelMpTitle: 'Mercado Pago',
  channelMpBody: 'Pago en el navegador con tu cuenta de Mercado Pago.',
} as const;

export const creceLegacyActivateCopy = {
  screenTitle: 'Activar plan Crece',
  badgeTitle: 'Tu plan original sigue',
  badgeBody:
    'Te registraste cuando Vuelto estaba arrancando. Como agradecimiento, seguís con las funciones de un plan de pago, sin abono. Gracias por bancarnos desde el día uno.',
  supportPitch:
    'Si valorás Vuelto y querés que sigamos mejorando el producto para todos, podés pasar al plan Crece. No te cambiamos lo que ya usás: es una forma de bancar el proyecto.',
  bullets: [
    'Gracias por estar desde el principio',
    'Tu plan original no se toca',
    'El plan Crece ayuda a sostener Vuelto (y al que arranca gratis)',
  ] as const,
  ctaPrimary: 'Pasarme al plan Crece',
  ctaSecondary: 'Seguir con mi plan original',
  /** Línea corta para la card de Facturación (el pitch largo queda en Activar). */
  managePitch: 'Si querés apoyar el proyecto, podés pasarte al plan Crece.',
} as const;

export const creceLoginCopy = {
  bannerTitle: 'Vas a activar el plan Crece',
  bannerBody: 'Primero tu cuenta. Después te llevamos a pagar.',
  tabSignup: 'Crear cuenta',
  tabLogin: 'Iniciar sesión',
  ctaSignup: 'Crear cuenta y seguir',
} as const;

export const crecePaywallTeamCopy = {
  eyebrow: 'Esto es de Crece',
  title: 'Invitá al equipo cuando el local lo pida',
  body: 'En Gratis atendés vos. Con Crece sumás permisos.',
  cta: 'Activar plan Crece · $25.000/mes',
  dismiss: 'Ahora no',
} as const;

export const crecePaywallBranchCopy = {
  eyebrow: 'Esto es de Crece',
  title: 'Otra sucursal, mismo Vuelto',
  body: 'El plan gratuito cubre una sucursal. Con el plan Crece sumás todas las que necesites.',
  cta: 'Activar plan Crece · $25.000/mes',
  dismiss: 'Ahora no',
} as const;

export const creceSuccessCopy = {
  title: 'Crece quedó activo',
  body: 'Ya podés sumar equipo y otra sucursal.',
  confirmingTitle: 'Estamos confirmando el pago',
  confirmingBody:
    'Ya podés usar el local; te avisamos cuando figure el plan Crece.',
  ctaHome: 'Ir a Inicio',
  ctaBilling: 'Ver Plan y facturación',
} as const;

export const creceManageCopy = {
  cancelTitle: 'Si cancelás',
  cancelBody:
    'Volvés al plan gratuito. Tu local, el catálogo y las ventas siguen. Lo que es exclusivo del plan Crece deja de estar disponible.',
  openMpSubscriptions: 'Abrir Mercado Pago · Suscripciones',
  openAppleSubscriptions: 'Abrir suscripciones de Apple',
  openGoogleSubscriptions: 'Abrir suscripciones de Google Play',
  cancelCta: 'Cancelar en el proveedor de pago',
  cancelMp: 'Cancelar en Mercado Pago',
  cancelApple: 'Cancelar en App Store',
  cancelGoogle: 'Cancelar en Google Play',
  /** Línea de precio + canal bajo el nombre del plan (p. ej. `$25.000 / mes · cobrado por Mercado Pago`). */
  priceProviderLine: (priceLabel: string, providerLabel: string) =>
    `${priceLabel} / mes · cobrado por ${providerLabel}`,
} as const;

export const creceEmployeeGateCopy = {
  title: 'El plan Crece lo activa el dueño',
  body: 'Pedile a quien administra el negocio que abra el link o entre a Plan y facturación.',
} as const;

/** `['a', 'b', 'c']` → `a, b y c`. */
const joinListEs = (items: string[]) =>
  items.length <= 1
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;

/** Soft limit estructural (cupo sucursales / usuarios) + tope mensual de volumen. */
export const creceSoftLimitCopy = {
  eyebrow: 'Plan gratuito',
  titleNear: 'Te estás acercando al tope de tu plan',
  /** Cupo estructural ya completo (estado normal del gratis: 1/1). El pitch real es el tope mensual. */
  titleAtLimit: 'Tu plan gratuito tiene un tope por mes',
  /**
   * `caps`: frases `{tope} {métrica}` del volumen activo (`250 ventas`, `25 turnos`).
   * Vacío = backend sin metrics: fallback al cupo estructural.
   */
  bodyAtLimit: (caps: string[]) =>
    `El plan gratis es para arrancar: ${
      caps.length > 0
        ? `hasta ${joinListEs(caps)} al mes`
        : 'cubre 1 sucursal y 1 usuario'
    }. Si tu negocio ya vende más que eso, este plan te queda chico.`,
  /** Título con cupo restante (p. ej. `Te quedan 2 sucursales`). */
  titleNearRemaining: (remaining: number, singular: string, plural: string) =>
    remaining === 1 ? `Te queda 1 ${singular}` : `Te quedan ${remaining} ${plural}`,
  bodyNear:
    'El plan gratuito tiene un cupo de sucursales y de gente en el equipo. Con el plan Crece sumás lo que necesites.',
  cta: 'Ver opciones de plan',
  dismiss: 'Seguir con el plan gratuito',
} as const;

/** Soft limits de volumen mensual: avisan, nunca cortan la caja. */
export const creceVolumeSoftLimitCopy = {
  eyebrow: 'Plan gratuito',
  titleNear: 'Este mes ya usaste bastante el plan gratuito',
  titleOver: 'Este mes superaste el tope del plan gratuito. ¡Felicitaciones!',
  bodyOver:
    'El plan Emprende te da más capacidad mensual. Si necesitás equipo o sucursales, el plan Crece no tiene tope.',
  finePrintOver: 'Podés seguir vendiendo igual — no te vamos a limitar.',
  titleNearRemaining: (remaining: number, singular: string, plural: string) =>
    remaining === 1
      ? `Te queda 1 ${singular} este mes`
      : `Te quedan ~${remaining} ${plural} este mes`,
  bodyNear:
    'El plan Emprende te da más margen para ventas, pedidos y turnos. El plan Crece suma uso sin tope, equipo y sucursales.',
  bodyNearPercent: (percent: number) =>
    `Usaste el ${percent}% del tope del plan gratuito. Con el plan Emprende tenés más capacidad.`,
  cta: 'Ver opciones de plan',
  dismiss: 'Seguir con el plan gratuito',
} as const;

export const emprendeVolumeSoftLimitCopy = {
  eyebrow: 'Plan Emprende',
  titleNear: 'Ya usaste buena parte de tu capacidad mensual',
  titleOver: 'Llegaste al tope del plan Emprende. ¡Tu negocio está en otro ritmo!',
  bodyOver: 'Con el plan Crece tenés ventas, pedidos y turnos sin tope, además de equipo y sucursales.',
  finePrintOver: 'Podés seguir operando igual — no se bloquea tu POS.',
  titleNearRemaining: (remaining: number, singular: string, plural: string) =>
    remaining === 1
      ? `Te queda 1 ${singular} este mes`
      : `Te quedan ~${remaining} ${plural} este mes`,
  bodyNear: 'El plan Crece no tiene tope mensual y suma equipo y sucursales.',
  bodyNearPercent: (percent: number) =>
    `Usaste el ${percent}% de la capacidad mensual del plan Emprende. El plan Crece no tiene tope.`,
  cta: 'Ver opciones de plan',
  dismiss: 'Seguir con el plan Emprende',
} as const;

/** Banner suave en Inicio para plan original (sin nudge de volumen ~80%). */
export const creceLegacyHomeCopy = {
  eyebrow: 'Plan original',
  title: 'Tu plan original sigue',
  body: 'Si querés apoyar el proyecto, podés pasarte al plan Crece. No te cambiamos lo que ya usás.',
  cta: 'Ver plan Crece',
  dismiss: 'Seguir con mi plan original',
} as const;

/** Nudge de volumen para plan original (legacy): agradecimiento, nunca exigencia. */
export const creceLegacyVolumeCopy = {
  eyebrow: 'Plan original',
  title: 'Tu negocio ya vende a otro ritmo',
  body: 'Con tu plan original no hay tope y eso no cambia — lo tenés ganado desde el día uno. Si querés apoyar el proyecto, el plan Crece es la forma.',
  cta: 'Ver opciones de plan',
  dismiss: 'Seguir con el plan original',
} as const;

export const creceMailCopy = {
  subject: 'Activaste Vuelto Crece',
  lead: 'Crece quedó activo.',
  priceLine: (priceLabel: string, channelLabel: string) =>
    `${priceLabel} · sin comisión por venta. El cobro lo maneja ${channelLabel}.`,
  security: 'Si no fuiste vos, escribinos a soporte.',
  cta: 'Abrir mi local',
} as const;

/** Canales de pago actuales + hueco futuro Stripe (otro hilo). */
export type CrecePaymentChannel = 'mercadopago' | 'iap' | 'stripe';
